import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RouterModule } from "@angular/router";
import { MatIcon } from "@angular/material/icon";
import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import { EquipoHoy, PersonaHoy } from "src/app/shared/model/security/equipo-hoy.model";
import { EquipoService } from "./equipo.service";

/** Una columna del bloque: título, icono, lista y cómo leer su detalle. */
interface ColumnaHoy {
  clave: "vacaciones" | "permisos" | "homeOffice" | "cumpleanos";
  titulo: string;
  icono: string;
  vacio: string;
  personas: PersonaHoy[];
}

/**
 * «Hoy en el equipo»: responde de un vistazo quién no está (vacaciones,
 * permiso), quién trabaja desde casa y quién cumple años esta semana.
 * Vive en Inicio, debajo del encabezado, para toda la empresa.
 */
@Component({
  selector: "app-hoy-en-el-equipo",
  standalone: true,
  templateUrl: "./hoy-en-el-equipo.component.html",
  styleUrls: ["./hoy-en-el-equipo.component.css"],
  imports: [CommonModule, RouterModule, MatIcon],
})
export class HoyEnElEquipoComponent implements OnInit {
  cargando = true;
  fallo = false;
  fecha: Date | null = null;
  /** Arranca con las cuatro columnas vacías para que el bloque no salte al cargar. */
  columnas: ColumnaHoy[] = this.columnasDe(new EquipoHoy());
  /** Cuántas personas no están hoy: vacaciones más faltas con permiso. */
  fueraHoy = 0;

  puedeVerCalendario = false;
  puedeVerMonitoreo = false;

  private readonly destroyRef = inject(DestroyRef);
  /** true desde que el bloque se destruye; guarda el repintado (ver repintar()). */
  private destruido = false;

  constructor(
    private equipoService: EquipoService,
    private authService: AuthService,
    // La vista NO se repinta sola cuando llega la respuesta HTTP: en este
    // arranque zone.js no queda parcheando XMLHttpRequest, asi que el
    // estado cambia y el bloque se queda en "Consultando..." hasta el
    // siguiente clic. Se pide el repintado a mano, el mismo patron que
    // usa el resto de la casa (asistencia/mi-asistencia).
    // NO quitar por "innecesario".
    private cdr: ChangeDetectorRef,
  ) {
    this.destroyRef.onDestroy(() => (this.destruido = true));
  }

  ngOnInit(): void {
    const menus = this.authService.getMenuList();
    this.puedeVerCalendario = tieneAccesoMenu(menus, "admin/vacaciones/calendario");
    this.puedeVerMonitoreo = tieneAccesoMenu(menus, "asistencia/monitoreo");
    this.consultar();
  }

  consultar(): void {
    this.cargando = true;
    this.fallo = false;
    this.equipoService.hoy().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (hoy) => {
        this.pintar(hoy || new EquipoHoy());
        this.cargando = false;
        this.repintar();
      },
      error: () => {
        this.fallo = true;
        this.cargando = false;
        this.repintar();
      },
    });
  }

  /**
   * Repinta el bloque. El guardia sigue haciendo falta aunque la
   * suscripcion lleve takeUntilDestroyed: al destruir, takeUntil cierra
   * el flujo y eso dispara el complete del observador, ya con la vista
   * marcada como destruida, donde detectChanges() truena.
   */
  private repintar(): void {
    if (!this.destruido) {
      this.cdr.detectChanges();
    }
  }

  /** El detalle tal como lo leería una persona, según la columna. */
  detalleDe(columna: ColumnaHoy, persona: PersonaHoy): string {
    const detalle = (persona.detalle || "").trim();
    switch (columna.clave) {
      case "vacaciones":
        return detalle ? `hasta el ${this.diaMes(detalle)}` : "";
      case "permisos":
        return this.leerPermiso(detalle);
      case "homeOffice":
        return this.leerHomeOffice(detalle);
      case "cumpleanos":
        return this.leerCumpleanos(persona.enDias, detalle);
    }
  }

  esHoy(columna: ColumnaHoy, persona: PersonaHoy): boolean {
    return columna.clave === "cumpleanos" && persona.enDias === 0;
  }

  private pintar(hoy: EquipoHoy): void {
    this.fecha = this.fechaLocal(hoy.fecha);
    const vacaciones = hoy.vacaciones || [];
    const permisos = hoy.permisos || [];
    this.fueraHoy =
      vacaciones.length + permisos.filter((p) => (p.detalle || "").toUpperCase().startsWith("FALTA")).length;
    this.columnas = this.columnasDe(hoy);
  }

  private columnasDe(hoy: EquipoHoy): ColumnaHoy[] {
    return [
      { clave: "vacaciones", titulo: "De vacaciones", icono: "beach_access", vacio: "Nadie está de vacaciones", personas: hoy.vacaciones || [] },
      { clave: "permisos", titulo: "Con permiso", icono: "event_busy", vacio: "Sin permisos para hoy", personas: hoy.permisos || [] },
      { clave: "homeOffice", titulo: "Desde casa", icono: "home_work", vacio: "Todos en oficina", personas: hoy.homeOffice || [] },
      { clave: "cumpleanos", titulo: "Cumpleaños de la semana", icono: "cake", vacio: "Nadie cumple esta semana", personas: hoy.cumpleanos || [] },
    ];
  }

  /** "2026-09-18" → "18/09". Lo que no se pueda leer se devuelve tal cual. */
  private diaMes(fecha: string): string {
    const partes = fecha.split("-");
    return partes.length === 3 ? `${partes[2]}/${partes[1]}` : fecha;
  }

  private leerPermiso(detalle: string): string {
    const [tipo, hora] = detalle.split(" ");
    switch ((tipo || "").toUpperCase()) {
      case "FALTA":
        return "No viene hoy";
      case "SALIDA":
        return hora ? `Sale a las ${hora}` : "Sale antes";
      case "ENTRADA":
        return hora ? `Llega a las ${hora}` : "Llega tarde";
      default:
        return detalle;
    }
  }

  private leerHomeOffice(detalle: string): string {
    switch (detalle.toLowerCase()) {
      case "todo el dia":
        return "Todo el día";
      case "manana":
        return "Por la mañana";
      case "tarde":
        return "Por la tarde";
      default:
        return detalle;
    }
  }

  private leerCumpleanos(enDias: number | null, diaMes: string): string {
    if (enDias === 0) {
      return "¡Hoy!";
    }
    if (enDias === 1) {
      return `Mañana · ${diaMes}`;
    }
    return enDias ? `En ${enDias} días · ${diaMes}` : diaMes;
  }

  /** yyyy-MM-dd a Date local sin que la zona horaria lo corra un día. */
  private fechaLocal(fecha: string | null): Date | null {
    if (!fecha) {
      return null;
    }
    const [anio, mes, dia] = fecha.split("-").map((n) => Number(n));
    return anio && mes && dia ? new Date(anio, mes - 1, dia) : null;
  }
}
